import { useEffect, useRef, useState } from 'react'
import NoSleep from 'nosleep.js'

// Keep the device screen awake while `active` is true.
//
// Two independent strategies, because a single one is unreliable on iOS —
// especially on PASSIVE display pages (course map / TV / photo wall) that get
// no taps, where a gesture-primed-only approach silently fails:
//
//   1. navigator.wakeLock (iOS 16.4+ / modern devices): requested WITHOUT a
//      user gesture, and re-asserted on visibilitychange AND on a short
//      interval. iOS releases the lock on any visibility blip / Low Power
//      Mode; the interval re-acquires it before the ~20s auto-lock, so an
//      unattended display stays awake with no taps. This was the piece the
//      NoSleep-only version lacked — hence displays slept.
//   2. Older iOS (no Wake Lock API): a muted, inline, looping <video> via
//      nosleep.js, primed on the first touch/click.
//
// Returns a short status string for UIs that want to surface it.
export default function useWakeLock(active) {
  const [status, setStatus] = useState('')
  const lockRef = useRef(null)
  const noSleepRef = useRef(null)

  useEffect(() => {
    if (!active) return
    let cancelled = false
    const hasNative =
      typeof navigator !== 'undefined' && 'wakeLock' in navigator

    // ── Strategy 1: native Wake Lock (no gesture needed) ──────────────────
    const requestNative = async () => {
      if (cancelled || !hasNative) return
      if (lockRef.current) return // already held — cheap no-op for the interval
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      try {
        const lock = await navigator.wakeLock.request('screen')
        if (cancelled) { try { await lock.release() } catch {} ; return }
        lockRef.current = lock
        setStatus('Screen will stay on.')
        // When iOS releases it (hidden tab, Low Power Mode), drop our handle
        // so the interval / visibility handler re-acquires it.
        lock.addEventListener?.('release', () => { lockRef.current = null })
      } catch (e) {
        lockRef.current = null
        console.warn('[wakeLock] native request failed (will retry):', e?.name || e?.message || e)
      }
    }

    // ── Strategy 2: nosleep.js muted-video fallback (older iOS only) ───────
    let videoPrimed = false
    const enableVideoFallback = async () => {
      if (cancelled || videoPrimed) return
      try {
        if (!noSleepRef.current) noSleepRef.current = new NoSleep()
        await noSleepRef.current.enable()
        videoPrimed = true
        setStatus('Screen will stay on.')
        removeGestureListeners()
      } catch {
        // Needs a user gesture — keep the listeners armed for the next tap.
        setStatus('Tap the screen once to keep it awake.')
      }
    }

    const onGesture = () => {
      if (hasNative) requestNative()
      else { videoPrimed = false; enableVideoFallback() }
    }
    const addGestureListeners = () => {
      document.addEventListener('touchend', onGesture, { passive: true })
      document.addEventListener('click', onGesture)
    }
    const removeGestureListeners = () => {
      document.removeEventListener('touchend', onGesture)
      document.removeEventListener('click', onGesture)
    }

    // iOS releases the lock / pauses the video when the page is hidden.
    const onVisibility = () => {
      if (cancelled || document.visibilityState !== 'visible') return
      if (hasNative) requestNative()
      else enableVideoFallback()
    }
    document.addEventListener('visibilitychange', onVisibility)

    // Periodic re-assert so a silent release on a passive display recovers
    // without a tap. No-ops when the lock is already held. 12s < ~20s
    // auto-lock, so the screen never gets a chance to dim.
    const interval = setInterval(() => {
      if (hasNative) requestNative()
    }, 12000)

    addGestureListeners()
    // Immediate attempt: native succeeds with no gesture; video waits for a tap.
    if (hasNative) requestNative()
    else enableVideoFallback()

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
      removeGestureListeners()
      try { lockRef.current?.release?.() } catch {}
      lockRef.current = null
      try { noSleepRef.current?.disable?.() } catch {}
      setStatus('')
    }
  }, [active])

  return status
}
